import {
  setElementExpanded,
  useIsElementExpanded,
} from '@client/hooks/use-expand-elements.hooks';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { useFlexStyles } from '@client/style';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ButtonProps,
  Theme,
  Typography,
} from '@mui/material';
import { FC, ReactNode, useCallback, useMemo } from 'react';
import type React from 'react';
import { makeStyles } from 'tss-react/mui';
import { IconButton } from '@ui/buttons';
import { Icon } from '@ui/Icon';

type NodonAccordionProps = {
  children?: ReactNode;
  summary: string;
  disablePadding?: boolean;

  /**
   * Provide a callback to show plus icon on the right side of the summary
   * @returns
   */
  onIconClick?: ButtonProps['onClick'];
};

export const SidePanelAccordionSection: FC<NodonAccordionProps> = ({
  children,
  summary,
  disablePadding,
  onIconClick,
}) => {
  const { classes, cx } = useStyles();
  const { classes: flex } = useFlexStyles();

  const expanded = useIsElementExpanded(summary);
  const readonly = useIsReadonly();

  const setExpanded = useCallback(
    (expanded: boolean) => setElementExpanded(summary, expanded),
    [summary],
  );

  const toggleExpandAccordion = useCallback(
    () => setExpanded(!expanded),
    [expanded, setExpanded],
  );

  const addIcon = useMemo(() => {
    if (onIconClick) {
      return (
        <IconButton
          category="mui"
          icon="Add"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            setExpanded(true);
            e.stopPropagation();
            onIconClick?.(e);
          }}
        />
      );
    }
    return null;
  }, [onIconClick, setExpanded]);

  return (
    <Accordion
      className={cx(classes.root, flex.displayFlex, flex.justifyContentStart)}
      expanded={expanded}
      elevation={0}
      onChange={toggleExpandAccordion}
      classes={{
        expanded: classes.expanded,
      }}
    >
      <AccordionSummary
        component="div"
        expandIcon={<Icon category="mui" icon="ExpandMore" />}
        classes={{
          content: classes.header,
          expanded: classes.expanded,
        }}
      >
        <Typography className={cx(classes.label)}>{summary}</Typography>
        {!readonly && addIcon}
      </AccordionSummary>

      <AccordionDetails
        className={cx(
          classes.details,
          disablePadding && classes.disablePadding,
        )}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    top: 1,
    backgroundColor: 'white',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    '&:not(:last-child)': {
      borderBottom: 5,
    },
    '&:before': {
      display: 'none',
    },
    borderLeftWidth: 0,
    borderRightWidth: 0,
    borderRadius: 0,
    '&:last-of-type': {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 0,
    marginBottom: 0,
  },

  label: {
    fontSize: theme.typography.pxToRem(15),
    lineHeight: '48px',
    fontWeight: theme.typography.fontWeightRegular,
    display: 'flex',
    alignItems: 'center',
  },

  expanded: {
    '&.Mui-expanded': {
      backgroundColor: 'whitesmoke',
      minHeight: 'unset',
      marginTop: 0,
      marginBottom: 0,
    },
  },

  details: {
    display: 'flex',
    flexDirection: 'column',
  },

  disablePadding: {
    padding: 0,
  },
}));
